import { DraftWaypointStatus } from '@caresend/types';
import { getWaypointStatusInfo, formatFullName, getLatestInListTimestamp } from '@caresend/utils';
import { getLocalDraftWaypointDispatchSteps } from '../../../../components/specific/DraftWaypointDetails/subComponents/StepperStatus/get.js';
import { useDraftWaypointOverviewCardData } from '../../../../components/specific/schedulingFlow/DraftWaypointOverviewCard/useDraftWaypointOverviewCardData.js';
import { getStore } from '../../../helpers/initStore.js';

/**
 * Updates the local draft waypoint to mimic the actions that happened in
 * cloud `confirmVisit`. Only data that is required to be displayed to the
 * user while algolia updates needs to be accurate.
 */
const setSchedulingFlowLocalDraftWaypointFinalized = () => {
    var _a;
    const store = getStore();
    const { draftWaypoint, labOptions, primaryContact, selectedDrawPlaceID, } = store.state.schedulingFlow;
    if (!draftWaypoint)
        throw Error('Missing draft waypoint');
    /**
     * If we have an integration enabled lab visit, we need to add the integration
     * to the local draft waypoint in order to enable the correct display of times on the visit details page.
     */
    const shouldHaveIntegration = selectedDrawPlaceID
        && store.getters['schedulingFlow/getLabHasAnyIntegration'](selectedDrawPlaceID);
    const integrationName = selectedDrawPlaceID
        ? (_a = labOptions === null || labOptions === void 0 ? void 0 : labOptions[selectedDrawPlaceID]) === null || _a === void 0 ? void 0 : _a.integration
        : undefined;
    const integration = shouldHaveIntegration && integrationName
        ? { name: integrationName, id: {} }
        : undefined;
    const updatedDraftWaypoint = {
        ...draftWaypoint,
        contact: primaryContact,
        drawPlaceID: selectedDrawPlaceID,
        status: DraftWaypointStatus.FINALIZED,
        integration,
    };
    store.commit('schedulingFlow/SET_DRAFT_WAYPOINT', updatedDraftWaypoint);
};
/**
 * Updates the local old draft waypoint to mimic the actions the happened in
 * cloud `confirmVisit` if the user is rescheduling. Only data that is required
 * to be displayed to the user while algolia updates needs to be accurate.
 */
const setSchedulingFlowLocalOldDraftWaypointCanceled = () => {
    const store = getStore();
    const { oldDraftWaypoint } = store.state.schedulingFlow.localState;
    if (!oldDraftWaypoint)
        throw Error('Missing old draft waypoint');
    const updatedOldDraftWaypoint = {
        ...oldDraftWaypoint,
        status: DraftWaypointStatus.CANCELED,
    };
    store.commit('schedulingFlow/SET_OLD_DRAFT_WAYPOINT', updatedOldDraftWaypoint);
};
/**
 * Places data may not be available on the patient visit list or page. Use this
 * function only if vuex place data is known to be stored. Currently used in
 * the scheduling flow and pro visit list.
 */
const getLocalFacilityDetailsForSchedulingOrProVisitList = (draftWaypoint) => {
    const store = getStore();
    const { drawPlaceID } = draftWaypoint;
    if (!drawPlaceID)
        return undefined;
    const { labOptions } = store.state.schedulingFlow;
    const labInfo = labOptions === null || labOptions === void 0 ? void 0 : labOptions[drawPlaceID];
    if (!labInfo)
        return undefined;
    const facilityDetails = {
        ...labInfo,
        id: labInfo.placeID,
        location: labInfo.location,
        openingHours: labInfo.openingHours,
        placeGroupID: labInfo.placeGroupID,
        name: labInfo.name,
        phoneNumber: labInfo.phoneNumber,
    };
    return facilityDetails;
};
const getLocalContactNameForScheduling = (draftWaypoint) => {
    const store = getStore();
    const { contact } = draftWaypoint;
    if (!contact)
        return undefined;
    // Custom contact
    if (contact.name)
        return contact.name;
    // User contact
    const { userID } = contact;
    if (!userID)
        return undefined;
    const currentUser = store.state.auth.user;
    if ((currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) === userID)
        return formatFullName(currentUser);
    const patientUsers = store.state.schedulingFlow.patients;
    if (patientUsers === null || patientUsers === void 0 ? void 0 : patientUsers[userID])
        return formatFullName(patientUsers[userID]);
    const { staffUsers } = store.state.users;
    if (staffUsers === null || staffUsers === void 0 ? void 0 : staffUsers[userID])
        return formatFullName(staffUsers[userID]);
    return undefined;
};
const getLocalPatientProcedureDataForScheduling = (draftWaypoint) => {
    const { localPatientProcedureData } = useDraftWaypointOverviewCardData({
        draftWaypointID: draftWaypoint.id,
    });
    return localPatientProcedureData.value;
};
const getLocalPrescriberInfoForScheduling = () => {
    const store = getStore();
    const { prescriber } = store.state.schedulingFlow;
    if (!prescriber)
        return undefined;
    return {
        userID: prescriber.id,
        formattedName: formatFullName(prescriber),
    };
};
const getLocalSchedulingUserNameForScheduling = () => {
    var _a, _b, _c, _d, _e, _f;
    const store = getStore();
    const orderID = (_b = (_a = store.state.schedulingFlow.draftWaypoint) === null || _a === void 0 ? void 0 : _a.draftOrderID) !== null && _b !== void 0 ? _b : (_d = Object.values((_c = store.state.schedulingFlow.procedures) !== null && _c !== void 0 ? _c : {})[0]) === null || _d === void 0 ? void 0 : _d.orderID;
    const schedulingUserID = (_f = (_e = store.state.orders.orders) === null || _e === void 0 ? void 0 : _e[orderID !== null && orderID !== void 0 ? orderID : '']) === null || _f === void 0 ? void 0 : _f.schedulingUserID;
    return formatFullName(store.getters['users/getStaffUserByID'](schedulingUserID));
};
const getLocalStaffingOfficeID = (draftWaypoint) => {
    const { staffingOffices } = draftWaypoint;
    if (!staffingOffices)
        return undefined;
    const latest = getLatestInListTimestamp(staffingOffices);
    return latest === null || latest === void 0 ? void 0 : latest.id;
};
/**
 * Using scheduling flow data, convert a draft waypoint to a denormalized
 * draft waypoint for display in the visit list/page while algolia updates.
 *
 * Requires the local draft waypoint to be modified to include similar changes
 * that will happen in cloud `confirmVisit` prior to passing.
 */
const getLocalDenormalizedDraftWaypointForScheduling = (draftWaypoint) => {
    var _a;
    const placeData = getLocalFacilityDetailsForSchedulingOrProVisitList(draftWaypoint);
    const localDraftWaypoint = {
        date: (_a = draftWaypoint.finalizedDate) === null || _a === void 0 ? void 0 : _a.date,
        dispatchSteps: getLocalDraftWaypointDispatchSteps(draftWaypoint, placeData),
        drawPlaceID: draftWaypoint.drawPlaceID,
        id: draftWaypoint.id,
        integration: draftWaypoint.integration,
        isLocalData: true,
        facilityDetails: placeData,
        location: draftWaypoint.location,
        patientProcedureData: getLocalPatientProcedureDataForScheduling(draftWaypoint),
        primaryContactName: getLocalContactNameForScheduling(draftWaypoint),
        prescriber: getLocalPrescriberInfoForScheduling(),
        procedureIDs: Object.keys(draftWaypoint.procedures),
        rescheduledFromID: draftWaypoint.rescheduledFromID,
        rescheduledToID: draftWaypoint.rescheduledToID,
        schedulingUserName: getLocalSchedulingUserNameForScheduling(),
        staffingOfficeID: getLocalStaffingOfficeID(draftWaypoint),
        status: draftWaypoint.status,
        statusInfo: getWaypointStatusInfo(draftWaypoint),
    };
    return localDraftWaypoint;
};

export { getLocalDenormalizedDraftWaypointForScheduling, getLocalFacilityDetailsForSchedulingOrProVisitList, setSchedulingFlowLocalDraftWaypointFinalized, setSchedulingFlowLocalOldDraftWaypointCanceled };
